import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Component, Injector, inject, resource } from '@angular/core';
import { RouterLink } from '@angular/router';
import { BbitTime, IBbitI18n } from '@bbitgmbh/bbit.utils';
import { BbitWebsiteService, I18nPipe } from '@bbitgmbh/website.sdk';
import { DateTime } from 'luxon';
import { BehaviorSubject, take } from 'rxjs';
import { IBbitPlaceBookingEvent } from '../../booking/booking/booking.component';
import { LoadingComponent } from '../../helper/loading/loading.component';
import { PlaceBookingCancellationOverlayComponent } from './place-booking-cancellation-overlay/place-booking-cancellation-overlay.component';
import { PlaceBookingCancellationOverlayRef } from './place-booking-cancellation-overlay/place-booking-cancellation-overlay.ref';
import { AccessComponent } from '../../access/access.component';

export interface IBbitPlaceBookingBookingCancelRequest {
  booking: string;
}

export interface IBbitPlaceBookingCancelResponse {
  success: boolean;
}

export interface IBbitPlaceBookingOwnBookingsResponse {
  success: boolean;
  bookings: IBbitPlaceBookingEvent[];
}

export interface IDisplayBooking {
  _id: string;
  time: string;
  place: IBbitI18n;
  booking: IBbitPlaceBookingEvent;
}

@Component({
  selector: 'app-place-bookings',
  templateUrl: './place-bookings.component.html',
  styleUrl: './place-bookings.component.scss',
  imports: [I18nPipe, LoadingComponent, RouterLink, AccessComponent],
})
export class PlaceBookingsComponent {
  private _breakpointObserver = inject(BreakpointObserver);
  public bookingsSubject = new BehaviorSubject<IDisplayBooking[]>([]);
  public bookings = resource({
    loader: async () => {
      const response = await fetch(`${this._website.getBlApiUrl()}/placeBooking/ownBookings`, {
        method: 'POST',
        headers: {
          Authorization: `${this._website.getAccessToken()}`,
        },
      });

      const result: IBbitPlaceBookingOwnBookingsResponse = await response.json();

      return result.bookings
        .sort((a, b) => {
          const dateA = BbitTime.fromISOTime(a.from).setToDateTime(DateTime.fromISO(a.date));
          const dateB = BbitTime.fromISOTime(b.from).setToDateTime(DateTime.fromISO(b.date));
          return dateA.toMillis() - dateB.toMillis();
        })
        .map((o) => {
          const date = DateTime.fromISO(o.date);
          const from = BbitTime.fromISOTime(o.from).setToDateTime(date);
          const until = BbitTime.fromISOTime(o.until).setToDateTime(date);
          const booking: IDisplayBooking = {
            _id: o._id,
            time: `${from.toFormat('EEEE, dd.LL.yyyy HH:mm')} - ${until.toFormat('HH:mm')}`,
            place: o.designation,
            booking: o,
          };
          return booking;
        });
    },
  });
  constructor(
    private _website: BbitWebsiteService,
    private _overlay: Overlay,
  ) {}

  isCancellationPossible(booking: IDisplayBooking): boolean {
    const now = DateTime.now().toMillis();
    const last = DateTime.fromISO(booking.booking.lastPossibleCancellationDate || '').toMillis();
    if (now <= last) {
      return true;
    }
    return false;
  }

  public async openCancellationOverlay(event: IDisplayBooking): Promise<void> {
    if (!this.isCancellationPossible(event)) {
      return;
    }
    const overlayConfig = new OverlayConfig({
      hasBackdrop: true,
      backdropClass: 'overlay-backdrop',
      panelClass: '',
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay.position().global().centerHorizontally().centerVertically(),
    });
    const overlayRef = this._overlay.create(overlayConfig);
    const overlayRefInstance = new PlaceBookingCancellationOverlayRef(overlayRef);
    overlayRefInstance
      .observeClose()
      .pipe(take(1))
      .subscribe((reload) => {
        if (reload) {
          this.bookings.reload();
        }
      });
    overlayRefInstance.setData({ event });
    const injector = Injector.create({
      providers: [
        { provide: PlaceBookingCancellationOverlayRef, useValue: overlayRefInstance },
        { provide: BbitWebsiteService, useValue: this._website },
      ],
    });
    const containerPortal = new ComponentPortal(PlaceBookingCancellationOverlayComponent, null, injector);
    const breakpoint = this._breakpointObserver.observe(Breakpoints.XSmall).subscribe((size) => {
      if (size.matches) {
        overlayRef.updateSize({ width: '100%', height: '100%' });
      } else {
        overlayRef.updateSize({
          minWidth: '400px',
          maxWidth: '600px',
          width: '80%',
          height: '80%',
          maxHeight: '80%',
        });
      }
    });
    overlayRef.attach(containerPortal);
    overlayRef.backdropClick().subscribe((): void => {
      breakpoint.unsubscribe();
      overlayRefInstance.close();
    });
  }
}
